/**
 * Minimal fetch wrapper for the onboarding BFF.
 * - Always uses `credentials: 'include'` (HttpOnly-cookie refresh token).
 * - Returns parsed JSON on 2xx.
 * - Throws `HttpError` carrying the HTTP status + parsed `ErrorEnvelope` on non-2xx.
 */

import { env } from '@/lib/env';
import { ROUTES } from '@/contracts/bff';
import type { ErrorEnvelope } from '@/contracts/bff';

/** A request that has not finished after this long is aborted, so a hung call cannot freeze a poll loop. */
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * Routes whose 401 means "wrong credentials" or "nobody signed in yet", not "the session expired": they must
 * never trigger the unauthorized handler (a failed sign-in would otherwise loop through signed-out).
 */
export const UNAUTHORIZED_EXEMPT_PATHS: readonly string[] = [
  ROUTES.authLogin,
  ROUTES.authCognitoLogin,
  ROUTES.authRegister,
  ROUTES.authCheckEmail,
  ROUTES.authSession,
  ROUTES.authRefresh,
  ROUTES.authLogout,
];

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly envelope: ErrorEnvelope | null,
    message: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/** The request did not finish within REQUEST_TIMEOUT_MS and was aborted. */
export class RequestTimeoutError extends Error {
  constructor(public readonly timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = 'RequestTimeoutError';
  }
}

async function parseErrorEnvelope(response: Response): Promise<ErrorEnvelope | null> {
  try {
    const json = await response.json();
    if (json && typeof json === 'object' && 'error' in json) {
      return json as ErrorEnvelope;
    }
  } catch {
    // Not JSON or not an envelope — fall through
  }
  return null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

/** In-memory Cognito ID token. Cookie is the primary session; Bearer is backup. */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registers the one callback run when a session call comes back 401 (the ID token expired or was revoked).
 * The AuthProvider uses it to flip to signed-out so the login gate re-prompts. Pass null to unregister.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
  };

  // Always send a JSON body for body-carrying methods. `Content-Type:
  // application/json` is set above, and Fastify rejects an empty body under that
  // content-type with `400 FST_ERR_CTP_EMPTY_JSON_BODY` before the handler runs.
  // Bodyless POSTs (refresh, logout) must therefore send at least `{}`.
  if (method !== 'GET' && method !== 'HEAD') {
    init.body = JSON.stringify(body ?? {});
  } else if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const url = `${env.bffUrl}${path}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  init.signal = controller.signal;

  try {
    const response = await fetch(url, init);

    if (!response.ok) {
      const envelope = await parseErrorEnvelope(response);
      const message = envelope?.error?.message ?? `HTTP ${response.status}`;
      if (response.status === 401 && !UNAUTHORIZED_EXEMPT_PATHS.includes(path)) unauthorizedHandler?.();
      throw new HttpError(response.status, envelope, message);
    }

    // 204 No Content — return empty object cast to T
    if (response.status === 204) {
      return {} as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    if (controller.signal.aborted && !(error instanceof HttpError)) throw new RequestTimeoutError(REQUEST_TIMEOUT_MS);
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export const httpClient = {
  get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    return request<T>(path, { method: 'GET', headers });
  },
  post<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return request<T>(path, { method: 'POST', body, headers });
  },
  put<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T> {
    return request<T>(path, { method: 'PUT', body, headers });
  },
};
